import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AlertCircle, ArrowRight, X } from "lucide-react";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { formatPlanDate, usePlanFeatures } from "@/hooks/usePlanFeatures";

const dismissKey = (workspaceId: string | undefined, periodEnd: string | null) =>
  `joylo_expired_plan_banner:${workspaceId ?? "none"}:${periodEnd ?? ""}`;

// A lapsed plan locks features everywhere, so say so everywhere instead of only on the billing page (JA-1018)
export const ExpiredPlanBanner = ({ linkState }: { linkState?: unknown }) => {
  const location = useLocation();
  const { activeWorkspace } = useWorkspace();
  const { isExpired, planName, currentPeriodEnd } = usePlanFeatures();
  const key = dismissKey(activeWorkspace?.id, currentPeriodEnd);
  const [dismissedKey, setDismissedKey] = useState<string | null>(() => {
    try {
      return sessionStorage.getItem(key) ? key : null;
    } catch {
      return null;
    }
  });

  if (!isExpired || location.pathname.startsWith("/billing")) return null;
  let dismissed = dismissedKey === key;
  if (!dismissed) {
    try {
      dismissed = Boolean(sessionStorage.getItem(key));
    } catch {
      /* storage can be unavailable in private windows */
    }
  }
  if (dismissed) return null;

  const endedOn = formatPlanDate(currentPeriodEnd);
  const dismiss = () => {
    try {
      sessionStorage.setItem(key, "1");
    } catch {
      /* dismissal then lasts only until the next render of a new shell */
    }
    setDismissedKey(key);
  };

  return (
    <div role="status" className="border-b border-danger/20 bg-danger-bg" data-testid="expired-plan-banner">
      <div className="mx-auto flex max-w-[1420px] items-center gap-3 px-4 py-2.5 sm:px-10">
        <AlertCircle className="h-4 w-4 shrink-0 text-danger" />
        <p className="min-w-0 flex-1 text-[13px] text-foreground">
          <span className="font-semibold">Your {planName} plan {endedOn ? `expired on ${endedOn}` : "has expired"}.</span>{" "}
          <span className="text-muted-foreground">Paid features in {activeWorkspace?.name ?? "this workspace"} are locked until it's renewed.</span>
        </p>
        <Link
          to="/billing/manage"
          state={linkState}
          className="inline-flex shrink-0 items-center gap-1 rounded-[8px] bg-gradient-sunset px-3 py-1.5 text-[12px] font-semibold text-white hover:opacity-95"
        >
          Renew <ArrowRight className="h-3.5 w-3.5" />
        </Link>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss for this session"
          className="shrink-0 rounded-md p-1 text-muted-foreground hover:bg-soft hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};
